'use client'

import { use, useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { ScatterChart, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { useAuth } from '@/contexts/AuthContext'

// Tier badge component for premium sections
function TierBadge({ tier }: { tier: 'collector' | 'gallery' | 'enterprise' }) {
  const colors = {
    collector: { bg: 'rgba(184, 92, 56, 0.15)', text: '#B85C38', label: 'Collector+' },
    gallery: { bg: 'rgba(43, 69, 112, 0.15)', text: '#2B4570', label: 'Gallery' },
    enterprise: { bg: 'rgba(139, 58, 42, 0.15)', text: '#8B3A2A', label: 'Enterprise' },
  }
  const { bg, text, label } = colors[tier]

  return (
    <span
      className="text-[10px] font-medium px-2 py-0.5 rounded-full uppercase tracking-wide"
      style={{ background: bg, color: text }}
    >
      {label}
    </span>
  )
}

// Expandable sale row component
function SaleRow({
  sale,
  artistColor
}: {
  sale: {
    work: string
    house: string
    hammer: string
    date: string
    estimate: string
    lotId?: string
    reserve?: string
    premium?: string
    confidence?: number
    provenance?: { owner: string; period: string }[]
    lotNotes?: string | null
  }
  artistColor: string
}) {
  const [expanded, setExpanded] = useState(false)
  const hasExpandableContent = sale.provenance && sale.provenance.length > 0

  return (
    <>
      <tr
        className={`border-b border-border-subtle transition-colors ${hasExpandableContent ? 'cursor-pointer hover:bg-page/50' : ''}`}
        onClick={() => hasExpandableContent && setExpanded(!expanded)}
      >
        <td className="py-3 text-sm">
          <div className="flex items-center gap-2">
            {hasExpandableContent && (
              <svg
                className={`w-4 h-4 text-text-tertiary transition-transform ${expanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
            {sale.lotId ? (
              <Link
                href={`/app/lots/${sale.lotId}`}
                className="hover:text-accent-primary transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                {sale.work}
              </Link>
            ) : (
              sale.work
            )}
          </div>
        </td>
        <td className="py-3 text-sm text-text-secondary">{sale.house}</td>
        <td className="py-3 text-sm text-right text-text-tertiary tabular-nums">{sale.reserve || sale.estimate}</td>
        <td className="py-3 text-sm text-right font-medium tabular-nums">{sale.hammer}</td>
        {sale.premium && (
          <td className="py-3 text-center">
            <span
              className="text-xs px-2 py-0.5 rounded-full"
              style={{ background: 'var(--positive)', color: 'white' }}
            >
              {sale.premium}
            </span>
          </td>
        )}
        {sale.confidence && (
          <td className="py-3 text-center">
            <span className="text-xs px-2 py-0.5 rounded-full bg-page text-text-tertiary">
              {sale.confidence}%
            </span>
          </td>
        )}
        <td className="py-3 text-sm text-right text-text-tertiary">{sale.date}</td>
      </tr>

      {/* Expanded Provenance Details */}
      <AnimatePresence>
        {expanded && sale.provenance && (
          <motion.tr
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <td colSpan={sale.premium ? 7 : 5} className="bg-page/30">
              <div className="px-8 py-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Provenance */}
                  <div>
                    <h5 className="text-xs font-medium text-text-secondary mb-3 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Provenance
                    </h5>
                    <div className="space-y-2">
                      {sale.provenance.map((p, i) => (
                        <div
                          key={i}
                          className="flex items-start gap-3 text-sm"
                        >
                          <div
                            className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0"
                            style={{ background: artistColor, opacity: 1 - i * 0.25 }}
                          />
                          <div>
                            <p className="text-text-primary">{p.owner}</p>
                            <p className="text-xs text-text-tertiary">{p.period}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Sale Details */}
                  <div>
                    <h5 className="text-xs font-medium text-text-secondary mb-3 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Sale Details
                    </h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-text-tertiary">Reserve Price</span>
                        <span>{sale.reserve || sale.estimate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-text-tertiary">Hammer Price</span>
                        <span className="font-medium">{sale.hammer}</span>
                      </div>
                      {sale.premium && (
                        <div className="flex justify-between">
                          <span className="text-text-tertiary">Premium over Reserve</span>
                          <span style={{ color: 'var(--positive)' }}>{sale.premium}</span>
                        </div>
                      )}
                      {sale.lotNotes && (
                        <div className="pt-2 mt-2 border-t border-border-subtle">
                          <p className="text-xs text-text-tertiary">
                            <span className="font-medium">Note:</span> {sale.lotNotes}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  )
}

const artistData: Record<string, {
  name: string
  nationality: string
  born: number
  died?: number
  tier: string
  movements: string[]
  medium: string
  marketCap: string
  avgHammer: string
  recordSale: string
  totalLots: string
  sellThrough: string
  buyInRate: string
  trend: number
  confidence: number
  color: string
  bio: string
  notableWorks: { title: string; year: string; medium: string; lastSale?: string }[]
  priceHistory: { date: string; work: string; value: number; reserve: number; hammer: number }[]
  recentSales: { work: string; house: string; hammer: string; date: string; estimate: string; lotId?: string; reserve?: string; premium?: string; confidence?: number; provenance?: { owner: string; period: string }[]; lotNotes?: string | null }[]
  upcomingAuctions: { work: string; house: string; estimate: string; date: string; lot: string }[]
  similarArtists: { name: string; slug: string; similarity: number }[]
  aiInsights: { title: string; content: string }[]
}> = {
  'yayoi-kusama': {
    name: 'Yayoi Kusama',
    nationality: 'Japanese',
    born: 1929,
    tier: 'Blue-chip',
    movements: ['Pop Art', 'Minimalism'],
    medium: 'Paintings, Sculpture, Installation',
    marketCap: '$2.4B',
    avgHammer: '$1.8M',
    recordSale: '$28.3M',
    totalLots: '4,892',
    sellThrough: '88%',
    buyInRate: '12%',
    trend: 18.4,
    confidence: 95,
    color: '#B85C38',
    bio: 'Yayoi Kusama is a Japanese contemporary artist who works primarily in sculpture and installation, and is also active in painting, film, and other media. Her work is based in conceptual art and shows some attributes of feminism, minimalism, surrealism, Art Brut, pop art, and abstract expressionism.',
    notableWorks: [
      { title: 'Infinity Mirror Rooms', year: '1965–present', medium: 'Installation', lastSale: '$14.5M' },
      { title: 'Pumpkin', year: '1994', medium: 'Painted bronze', lastSale: '$8.2M' },
      { title: 'Infinity Nets', year: '1958–present', medium: 'Acrylic on canvas', lastSale: '$28.3M' },
      { title: 'Dots Obsession', year: '2000s', medium: 'Mixed media', lastSale: '$4.1M' },
    ],
    priceHistory: [
      { date: '2019-03', work: 'Infinity Nets (TWAQ)', value: 4.2, reserve: 3.5, hammer: 4.2 },
      { date: '2019-11', work: 'Pumpkin (Yellow)', value: 2.8, reserve: 2.0, hammer: 2.8 },
      { date: '2020-06', work: 'Dots Obsession', value: 1.6, reserve: 1.2, hammer: 1.6 },
      { date: '2021-05', work: 'Infinity Nets (OQPW)', value: 7.8, reserve: 5.0, hammer: 7.8 },
      { date: '2021-11', work: 'Pumpkin (T.W.)', value: 5.1, reserve: 3.5, hammer: 5.1 },
      { date: '2022-05', work: 'Infinity Nets (QRTW)', value: 10.5, reserve: 7.0, hammer: 10.5 },
      { date: '2022-11', work: 'Yellow Pumpkin', value: 6.2, reserve: 4.5, hammer: 6.2 },
      { date: '2023-05', work: 'Infinity Nets (ABCD)', value: 8.4, reserve: 6.0, hammer: 8.4 },
      { date: '2023-11', work: 'Dots Obsession', value: 2.1, reserve: 1.5, hammer: 2.1 },
      { date: '2024-01', work: 'Infinity Nets (QRTWE)', value: 8.9, reserve: 6.0, hammer: 8.9 },
    ],
    recentSales: [
      { work: 'Infinity Nets (TWHOQ)', house: "Sotheby's", hammer: '$8.9M', date: 'Feb 2024', estimate: '$6-8M', lotId: 'SOT-2024-3291', reserve: '$6.0M', premium: '+48%', confidence: 99, provenance: [{ owner: 'Private Collection, Tokyo', period: '2005-2024' }, { owner: 'Gallery Ota Fine Arts', period: '2002-2005' }, { owner: 'Artist Studio', period: '1998-2002' }], lotNotes: 'Accompanied by certificate of authenticity from the artist studio' },
      { work: 'Infinity Nets (QRTWE)', house: "Christie's", hammer: '$8.2M', date: 'Jan 2024', estimate: '$6-8M', reserve: '$6.0M', premium: '+37%', confidence: 98, provenance: [{ owner: 'European Private Collection', period: '2010-2024' }, { owner: 'Victoria Miro Gallery', period: '2008-2010' }], lotNotes: null },
      { work: 'Pumpkin (T.W.B)', house: "Sotheby's", hammer: '$4.2M', date: 'Dec 2023', estimate: '$3-4M', reserve: '$3.0M', premium: '+40%', confidence: 98, provenance: [{ owner: 'European Private Collection', period: '2010-2023' }, { owner: 'Victoria Miro Gallery, London', period: '2008-2010' }], lotNotes: 'Exhibited at Tate Modern retrospective, 2012' },
      { work: 'Dots Obsession', house: 'Phillips', hammer: '$2.1M', date: 'Nov 2023', estimate: '$1.5-2M', reserve: '$1.5M', premium: '+40%', confidence: 97, provenance: [{ owner: 'Private Collection, Hong Kong', period: '2015-2023' }, { owner: 'David Zwirner Gallery', period: '2012-2015' }], lotNotes: null },
      { work: 'Infinity Nets (OQAB)', house: "Christie's", hammer: '$6.4M', date: 'Oct 2023', estimate: '$5-7M', reserve: '$5.0M', premium: '+28%', confidence: 97, provenance: [{ owner: 'American Private Collection', period: '2008-2023' }], lotNotes: null },
    ],
    upcomingAuctions: [
      { work: 'Infinity Nets (Red)', house: "Christie's", estimate: '$5-7M', date: 'Mar 15, 2024', lot: '34' },
      { work: 'Pumpkin (Yellow)', house: "Sotheby's", estimate: '$2-3M', date: 'Mar 22, 2024', lot: '18' },
      { work: 'Dots Accumulation', house: 'Phillips', estimate: '$800K-1.2M', date: 'Apr 5, 2024', lot: '42' },
    ],
    similarArtists: [
      { name: 'Takashi Murakami', slug: 'takashi-murakami', similarity: 92 },
      { name: 'Yoshitomo Nara', slug: 'yoshitomo-nara', similarity: 88 },
      { name: 'KAWS', slug: 'kaws', similarity: 84 },
    ],
    aiInsights: [
      { title: 'Market Momentum', content: "Kusama's market shows exceptional strength with 18.4% growth YoY. The 88% sell-through rate indicates robust collector demand. Works from the 1960s-70s command the highest premiums." },
      { title: 'Price Drivers', content: 'Infinity Nets paintings from the early period (1960s) trade at 40% premium to recent works. Pumpkin sculptures remain consistently strong performers. Scale matters - larger works outperform.' },
      { title: 'Collector Demographics', content: 'Strong Asian collector base, particularly from Japan, Hong Kong, and China. Growing Western institutional interest following major museum retrospectives.' },
    ],
  },
  'jean-michel-basquiat': {
    name: 'Jean-Michel Basquiat',
    nationality: 'American',
    born: 1960,
    died: 1988,
    tier: 'Blue-chip',
    movements: ['Neo-Expressionism', 'Street Art'],
    medium: 'Paintings, Drawings',
    marketCap: '$4.1B',
    avgHammer: '$3.2M',
    recordSale: '$110.5M',
    totalLots: '2,341',
    sellThrough: '92%',
    buyInRate: '8%',
    trend: 12.1,
    confidence: 97,
    color: '#2B4570',
    bio: 'Jean-Michel Basquiat was an American artist of Haitian and Puerto Rican descent. Basquiat first achieved fame as part of SAMO, an informal graffiti duo who wrote enigmatic epigrams in Manhattan in the late 1970s. He later painted neo-expressionist works that dealt with dichotomies such as wealth versus poverty, integration versus segregation.',
    notableWorks: [
      { title: 'Untitled (Skull)', year: '1981', medium: 'Acrylic and mixed media on canvas', lastSale: '$110.5M' },
      { title: 'Dustheads', year: '1982', medium: 'Acrylic and oil paintstick on canvas', lastSale: '$48.8M' },
      { title: 'Warrior', year: '1982', medium: 'Acrylic and oilstick on wood', lastSale: '$41.9M' },
      { title: 'El Gran Espectaculo', year: '1983', medium: 'Acrylic on canvas', lastSale: '$18.7M' },
    ],
    priceHistory: [
      { date: '2019-05', work: 'Untitled (Yellow)', value: 18.5, reserve: 15.0, hammer: 18.5 },
      { date: '2019-11', work: 'Dustheads Study', value: 8.2, reserve: 6.0, hammer: 8.2 },
      { date: '2020-10', work: 'Head', value: 12.4, reserve: 10.0, hammer: 12.4 },
      { date: '2021-05', work: 'In This Case', value: 93.1, reserve: 50.0, hammer: 93.1 },
      { date: '2021-11', work: 'The Field', value: 25.6, reserve: 20.0, hammer: 25.6 },
      { date: '2022-05', work: 'Versus Medici', value: 35.0, reserve: 25.0, hammer: 35.0 },
      { date: '2022-11', work: 'El Gran Espectaculo', value: 22.0, reserve: 15.0, hammer: 22.0 },
      { date: '2023-05', work: 'Warrior', value: 12.3, reserve: 10.0, hammer: 12.3 },
      { date: '2023-11', work: 'Untitled (Head)', value: 8.5, reserve: 6.0, hammer: 8.5 },
      { date: '2024-02', work: 'Untitled (Devil)', value: 42.4, reserve: 35.0, hammer: 42.4 },
    ],
    recentSales: [
      { work: 'Untitled (Devil)', house: "Christie's", hammer: '$42.4M', date: 'Feb 2024', estimate: '$35-45M', lotId: 'CHR-2024-1847', reserve: '$35.0M', premium: '+21%', confidence: 99, provenance: [{ owner: 'Private Collection, New York', period: '1998-2024' }, { owner: 'Bruno Bischofberger Gallery, Zurich', period: '1984-1998' }, { owner: 'Acquired directly from the artist', period: '1983-1984' }], lotNotes: 'Authenticated by the Authentication Committee of the Estate of Jean-Michel Basquiat' },
      { work: 'El Gran Espectaculo', house: "Sotheby's", hammer: '$18.7M', date: 'Jan 2024', estimate: '$15-20M', reserve: '$15.0M', premium: '+25%', confidence: 98, provenance: [{ owner: 'American Private Collection', period: '2005-2024' }, { owner: 'Gagosian Gallery', period: '2002-2005' }, { owner: 'Private Collection, Germany', period: '1988-2002' }], lotNotes: 'Featured in Whitney Museum exhibition, 1992' },
      { work: 'Warrior', house: "Christie's", hammer: '$12.3M', date: 'Nov 2023', estimate: '$10-15M', reserve: '$10.0M', premium: '+23%', confidence: 99, provenance: [{ owner: 'Asian Private Collector', period: '2016-2023' }, { owner: "Christie's Hong Kong", period: '2016' }, { owner: 'European Collection', period: '1990-2016' }], lotNotes: null },
      { work: 'Untitled (Head)', house: 'Phillips', hammer: '$8.5M', date: 'Oct 2023', estimate: '$6-8M', reserve: '$6.0M', premium: '+42%', confidence: 97, provenance: [{ owner: 'Private Collection', period: '2000-2023' }], lotNotes: null },
      { work: 'King Pleasure', house: "Sotheby's", hammer: '$6.2M', date: 'Sep 2023', estimate: '$4-6M', reserve: '$4.0M', premium: '+55%', confidence: 96, provenance: [{ owner: 'Private Collection, Miami', period: '1995-2023' }], lotNotes: null },
    ],
    upcomingAuctions: [
      { work: 'Untitled (Crown)', house: "Christie's", estimate: '$20-30M', date: 'May 14, 2024', lot: '12' },
      { work: 'SAMO Tags', house: "Sotheby's", estimate: '$5-7M', date: 'May 18, 2024', lot: '28' },
    ],
    similarArtists: [
      { name: 'Keith Haring', slug: 'keith-haring', similarity: 91 },
      { name: 'Andy Warhol', slug: 'andy-warhol', similarity: 87 },
      { name: 'Cy Twombly', slug: 'cy-twombly', similarity: 82 },
    ],
    aiInsights: [
      { title: 'Market Position', content: "Basquiat remains one of the most sought-after post-war artists. The 92% sell-through rate is exceptional for this price tier. Museum exhibitions continue to drive collector interest." },
      { title: 'Investment Outlook', content: 'Works featuring skulls, crowns, and text command highest premiums. The 1982-83 period represents peak output and prices. Authentication by the estate is critical for value.' },
      { title: 'Risk Factors', content: 'Market concentration at the top end - the majority of value comes from major works. Mid-tier works ($1-5M) showing strongest growth potential.' },
    ],
  },
  'gerhard-richter': {
    name: 'Gerhard Richter',
    nationality: 'German',
    born: 1932,
    tier: 'Blue-chip',
    movements: ['Abstract Expressionism', 'Photorealism'],
    medium: 'Paintings',
    marketCap: '$3.8B',
    avgHammer: '$2.9M',
    recordSale: '$46.3M',
    totalLots: '3,127',
    sellThrough: '85%',
    buyInRate: '15%',
    trend: 5.2,
    confidence: 94,
    color: '#D4956A',
    bio: 'Gerhard Richter is a German visual artist widely regarded as one of the most important contemporary artists. His work includes abstract and photorealistic paintings, photographs, and glass pieces. He has produced abstract paintings using a "squeegee" technique, as well as photorealistic works based on found photographs.',
    notableWorks: [
      { title: 'Abstraktes Bild (599)', year: '1986', medium: 'Oil on canvas', lastSale: '$46.3M' },
      { title: 'Domplatz, Mailand', year: '1968', medium: 'Oil on canvas', lastSale: '$37.1M' },
      { title: 'Abstraktes Bild (809-4)', year: '1994', medium: 'Oil on canvas', lastSale: '$34.2M' },
      { title: 'Eisberg', year: '1982', medium: 'Oil on canvas', lastSale: '$21.6M' },
    ],
    priceHistory: [
      { date: '2019-05', work: 'Abstraktes Bild (756)', value: 15.2, reserve: 12.0, hammer: 15.2 },
      { date: '2019-11', work: 'Seestück (Grey)', value: 4.8, reserve: 4.0, hammer: 4.8 },
      { date: '2020-10', work: 'Abstraktes Bild (712)', value: 8.6, reserve: 8.0, hammer: 8.6 },
      { date: '2021-05', work: 'Abstraktes Bild (940)', value: 22.5, reserve: 18.0, hammer: 22.5 },
      { date: '2021-11', work: 'Wolke (Cloud)', value: 12.1, reserve: 10.0, hammer: 12.1 },
      { date: '2022-05', work: 'Abstraktes Bild (648)', value: 28.0, reserve: 20.0, hammer: 28.0 },
      { date: '2022-11', work: 'Kerze (Candle)', value: 9.5, reserve: 8.0, hammer: 9.5 },
      { date: '2023-05', work: 'Abstraktes Bild (899)', value: 14.2, reserve: 12.0, hammer: 14.2 },
      { date: '2023-10', work: 'Seestück', value: 5.7, reserve: 5.0, hammer: 5.7 },
      { date: '2024-02', work: 'Abstraktes Bild (648-3)', value: 18.2, reserve: 15.0, hammer: 18.2 },
    ],
    recentSales: [
      { work: 'Abstraktes Bild (648-3)', house: 'Phillips', hammer: '$18.2M', date: 'Feb 2024', estimate: '$15-20M', lotId: 'PHL-2024-0892', reserve: '$15.0M', premium: '+21%', confidence: 97, provenance: [{ owner: 'European Private Collection', period: '2008-2024' }, { owner: 'Marian Goodman Gallery, New York', period: '2004-2008' }, { owner: 'Acquired from the artist', period: '2003-2004' }], lotNotes: 'Included in catalogue raisonné' },
      { work: 'Kerze', house: "Christie's", hammer: '$12.4M', date: 'Jan 2024', estimate: '$10-15M', reserve: '$10.0M', premium: '+24%', confidence: 98, provenance: [{ owner: 'Private Collection, Switzerland', period: '1995-2024' }], lotNotes: null },
      { work: 'Abstraktes Bild (780-1)', house: "Sotheby's", hammer: '$9.8M', date: 'Nov 2023', estimate: '$8-12M', reserve: '$8.0M', premium: '+23%', confidence: 96, provenance: [{ owner: 'American Private Collection', period: '2005-2023' }], lotNotes: null },
      { work: 'Seestück (Seascape)', house: "Christie's", hammer: '$7.2M', date: 'Oct 2023', estimate: '$5-7M', reserve: '$5.0M', premium: '+44%', confidence: 98, provenance: [{ owner: 'American Private Collection', period: '2000-2023' }, { owner: "Anthony d'Offay Gallery, London", period: '1998-2000' }], lotNotes: 'Exhibited at Centre Pompidou retrospective, 2012' },
    ],
    upcomingAuctions: [
      { work: 'Abstraktes Bild (Blue)', house: "Sotheby's", estimate: '$12-18M', date: 'Apr 20, 2024', lot: '8' },
    ],
    similarArtists: [
      { name: 'Sigmar Polke', slug: 'sigmar-polke', similarity: 89 },
      { name: 'Anselm Kiefer', slug: 'anselm-kiefer', similarity: 84 },
      { name: 'Georg Baselitz', slug: 'georg-baselitz', similarity: 81 },
    ],
    aiInsights: [
      { title: 'Market Analysis', content: "Richter's squeegee abstracts (1980s-90s) command highest prices. Photo-paintings showing renewed collector interest. Market stable with 5.2% growth YoY." },
      { title: 'Investment Thesis', content: 'Candle paintings (Kerze series) are highly liquid. Color-rich abstracts outperform gray-palette works. Catalogue raisonné inclusion essential.' },
      { title: 'Collector Trends', content: 'Strong institutional demand for museum-quality works. European collectors most active. Increasing Asian interest in abstract works.' },
    ],
  },
  'banksy': {
    name: 'Banksy',
    nationality: 'British',
    born: 1974,
    tier: 'Blue-chip',
    movements: ['Street Art', 'Pop Art'],
    medium: 'Paintings, Prints, Street Art',
    marketCap: '$1.2B',
    avgHammer: '$890K',
    recordSale: '$25.4M',
    totalLots: '5,892',
    sellThrough: '94%',
    buyInRate: '6%',
    trend: 8.7,
    confidence: 92,
    color: '#4A7C59',
    bio: 'Banksy is an anonymous England-based street artist, political activist, and film director. His satirical street art combines dark humor with graffiti executed in a distinctive stenciling technique. His works have appeared on streets, walls, and bridges throughout the world.',
    notableWorks: [
      { title: 'Love is in the Bin', year: '2018', medium: 'Spray paint on canvas', lastSale: '$25.4M' },
      { title: 'Devolved Parliament', year: '2009', medium: 'Oil on canvas', lastSale: '$12.1M' },
      { title: 'Girl with Balloon', year: '2006', medium: 'Spray paint on canvas', lastSale: '$18.8M' },
      { title: 'Game Changer', year: '2020', medium: 'Oil on canvas', lastSale: '$23.1M' },
    ],
    priceHistory: [
      { date: '2019-06', work: 'Devolved Parliament', value: 12.1, reserve: 2.0, hammer: 12.1 },
      { date: '2019-10', work: 'Girl with Balloon', value: 1.4, reserve: 0.5, hammer: 1.4 },
      { date: '2020-03', work: 'Show Me the Monet', value: 9.8, reserve: 4.0, hammer: 9.8 },
      { date: '2021-03', work: 'Game Changer', value: 23.1, reserve: 3.5, hammer: 23.1 },
      { date: '2021-10', work: 'Love is in the Bin', value: 25.4, reserve: 5.0, hammer: 25.4 },
      { date: '2022-03', work: 'Sunflowers from Petrol Station', value: 5.2, reserve: 2.5, hammer: 5.2 },
      { date: '2022-11', work: 'Pulp Fiction', value: 3.8, reserve: 2.0, hammer: 3.8 },
      { date: '2023-06', work: 'Laugh Now', value: 4.2, reserve: 3.0, hammer: 4.2 },
      { date: '2023-10', work: 'Flower Thrower', value: 3.1, reserve: 2.0, hammer: 3.1 },
      { date: '2024-01', work: 'Girl with Balloon', value: 25.0, reserve: 20.0, hammer: 25.0 },
    ],
    recentSales: [
      { work: 'Girl with Balloon', house: "Sotheby's", hammer: '$25.0M', date: 'Jan 2024', estimate: '$20-30M', lotId: 'SOT-2024-3187', reserve: '$20.0M', premium: '+25%', confidence: 99, provenance: [{ owner: 'Private Collection', period: '2006-2024' }], lotNotes: 'Pest Control authenticated' },
      { work: 'Girl with Balloon (signed)', house: "Sotheby's", hammer: '$7.8M', date: 'Dec 2023', estimate: '$5-7M', reserve: '$5.0M', premium: '+56%', confidence: 98, provenance: [{ owner: 'European Collection', period: '2010-2023' }], lotNotes: null },
      { work: 'Laugh Now', house: "Christie's", hammer: '$4.2M', date: 'Nov 2023', estimate: '$3-4M', reserve: '$3.0M', premium: '+40%', confidence: 97, provenance: [{ owner: 'UK Private Collection', period: '2008-2023' }], lotNotes: null },
      { work: 'Flower Thrower', house: 'Phillips', hammer: '$3.1M', date: 'Oct 2023', estimate: '$2-3M', reserve: '$2.0M', premium: '+55%', confidence: 97, provenance: [{ owner: 'American Collection', period: '2012-2023' }], lotNotes: null },
      { work: 'Pulp Fiction', house: "Sotheby's", hammer: '$2.8M', date: 'Sep 2023', estimate: '$2-3M', reserve: '$2.0M', premium: '+40%', confidence: 96, provenance: [{ owner: 'Private Collection, Los Angeles', period: '2005-2023' }], lotNotes: null },
    ],
    upcomingAuctions: [
      { work: 'Love Rat', house: "Christie's", estimate: '$2-3M', date: 'Mar 28, 2024', lot: '22' },
      { work: 'Rude Copper', house: 'Phillips', estimate: '$1.5-2M', date: 'Apr 10, 2024', lot: '45' },
    ],
    similarArtists: [
      { name: 'KAWS', slug: 'kaws', similarity: 87 },
      { name: 'Invader', slug: 'invader', similarity: 85 },
      { name: 'Mr. Brainwash', slug: 'mr-brainwash', similarity: 78 },
    ],
    aiInsights: [
      { title: 'Market Dynamics', content: 'Exceptionally high sell-through (94%) reflects broad collector appeal. Print editions provide accessible entry point. Unique works increasingly rare at auction.' },
      { title: 'Authentication', content: 'Pest Control authentication critical for value. Unsigned works trade at 40-60% discount. Provenance from early exhibitions commands premium.' },
      { title: 'Collector Base', content: 'Diverse global collector base. Strong appeal to new collectors. Social media impact continues to drive demand and visibility.' },
    ],
  },
  'amoako-boafo': {
    name: 'Amoako Boafo',
    nationality: 'Ghanaian',
    born: 1984,
    tier: 'Mid-market',
    movements: ['African Contemporary', 'Figurative Revival'],
    medium: 'Paintings',
    marketCap: '$85M',
    avgHammer: '$420K',
    recordSale: '$3.4M',
    totalLots: '127',
    sellThrough: '96%',
    buyInRate: '4%',
    trend: 42.1,
    confidence: 88,
    color: '#8B3A2A',
    bio: 'Amoako Boafo is a Ghanaian artist known for his vivid, large-scale portraits that celebrate Black identity and joy. His distinctive finger-painting technique creates textured, expressive works that have captured global attention. Based in Vienna, he has quickly risen to prominence in the contemporary art world.',
    notableWorks: [
      { title: 'Green Beret', year: '2020', medium: 'Oil on canvas', lastSale: '$3.4M' },
      { title: 'The Lemon Bathing Suit', year: '2019', medium: 'Oil on canvas', lastSale: '$1.1M' },
      { title: 'Cobalt Blue Shirt', year: '2020', medium: 'Oil on canvas', lastSale: '$881K' },
      { title: 'Self Portrait', year: '2019', medium: 'Oil on canvas', lastSale: '$650K' },
    ],
    priceHistory: [
      { date: '2020-02', work: 'The Lemon Bathing Suit', value: 0.88, reserve: 0.04, hammer: 0.88 },
      { date: '2020-07', work: 'Green Beret', value: 0.67, reserve: 0.12, hammer: 0.67 },
      { date: '2021-03', work: 'Cobalt Blue Shirt', value: 0.88, reserve: 0.35, hammer: 0.88 },
      { date: '2021-10', work: 'Self Portrait', value: 0.65, reserve: 0.25, hammer: 0.65 },
      { date: '2022-05', work: 'Green Beret', value: 3.4, reserve: 0.8, hammer: 3.4 },
      { date: '2022-11', work: 'Pink Interior', value: 0.52, reserve: 0.3, hammer: 0.52 },
      { date: '2023-05', work: 'Blue Shirt', value: 0.78, reserve: 0.4, hammer: 0.78 },
      { date: '2023-11', work: 'Yellow Background', value: 0.52, reserve: 0.3, hammer: 0.52 },
      { date: '2024-01', work: 'Hands Up', value: 0.78, reserve: 0.4, hammer: 0.78 },
    ],
    recentSales: [
      { work: 'Green Beret', house: "Christie's", hammer: '$3.4M', date: 'Jan 2024', estimate: '$800K-1.2M', lotId: 'CHR-2024-1901', reserve: '$800K', premium: '+325%', confidence: 99, provenance: [{ owner: 'Private Collection, Los Angeles', period: '2020-2024' }, { owner: 'Roberts Projects', period: '2020' }], lotNotes: 'Artist record at auction' },
      { work: 'Hands Up', house: "Sotheby's", hammer: '$780K', date: 'Dec 2023', estimate: '$400-600K', reserve: '$400K', premium: '+95%', confidence: 97, provenance: [{ owner: 'European Collection', period: '2021-2023' }], lotNotes: null },
      { work: 'Yellow Background', house: 'Phillips', hammer: '$520K', date: 'Nov 2023', estimate: '$300-400K', reserve: '$300K', premium: '+73%', confidence: 96, provenance: [{ owner: 'American Collection', period: '2020-2023' }], lotNotes: null },
    ],
    upcomingAuctions: [
      { work: 'Blue Interior', house: "Christie's", estimate: '$400-600K', date: 'Mar 20, 2024', lot: '56' },
    ],
    similarArtists: [
      { name: 'Otis Kwame Kye Quaicoe', slug: 'otis-kwame-kye-quaicoe', similarity: 91 },
      { name: 'Njideka Akunyili Crosby', slug: 'njideka-akunyili-crosby', similarity: 86 },
      { name: 'Toyin Ojih Odutola', slug: 'toyin-ojih-odutola', similarity: 83 },
    ],
    aiInsights: [
      { title: 'Explosive Growth', content: 'Market has grown 42% YoY with exceptional 96% sell-through rate. Strong institutional backing from major museums acquiring work. Primary market remains tightly controlled.' },
      { title: 'Collecting Thesis', content: 'Works with bold color backgrounds performing strongest. Large-scale portraits (over 60 inches) command premium. Series works from Vienna period most sought after.' },
      { title: 'Risk Assessment', content: 'Rapid price appreciation may indicate overheating. Limited secondary supply constrains liquidity. Watch for primary market pricing signals.' },
    ],
  },
  'flora-yukhnovich': {
    name: 'Flora Yukhnovich',
    nationality: 'British',
    born: 1990,
    tier: 'Mid-market',
    movements: ['Figurative Revival', 'Neo-Rococo'],
    medium: 'Paintings',
    marketCap: '$42M',
    avgHammer: '$580K',
    recordSale: '$2.9M',
    totalLots: '48',
    sellThrough: '100%',
    buyInRate: '0%',
    trend: 28.4,
    confidence: 85,
    color: '#C9A7A3',
    bio: 'Flora Yukhnovich is a British painter known for her exuberant, gestural works that reimagine Rococo sensibilities through a contemporary lens. Her paintings feature sweeping brushstrokes in candy-colored palettes, often referencing Old Masters while addressing themes of femininity and pleasure.',
    notableWorks: [
      { title: "I'll Have What She's Having", year: '2020', medium: 'Oil on linen', lastSale: '$2.9M' },
      { title: 'Warm, Blushing, Uncontrollable', year: '2021', medium: 'Oil on linen', lastSale: '$1.6M' },
      { title: 'The Pleasure Principle', year: '2019', medium: 'Oil on canvas', lastSale: '$890K' },
    ],
    priceHistory: [
      { date: '2020-10', work: "I'll Have What She's Having", value: 2.9, reserve: 0.05, hammer: 2.9 },
      { date: '2021-03', work: 'Warm, Blushing', value: 1.6, reserve: 0.25, hammer: 1.6 },
      { date: '2021-10', work: 'The Pleasure Principle', value: 0.89, reserve: 0.15, hammer: 0.89 },
      { date: '2022-03', work: 'Pink Study', value: 0.72, reserve: 0.3, hammer: 0.72 },
      { date: '2022-10', work: 'Sweetness', value: 0.85, reserve: 0.35, hammer: 0.85 },
      { date: '2023-05', work: 'Pastel Dreams', value: 0.72, reserve: 0.3, hammer: 0.72 },
      { date: '2024-01', work: 'The Pleasure Principle', value: 0.89, reserve: 0.4, hammer: 0.89 },
    ],
    recentSales: [
      { work: 'The Pleasure Principle', house: "Sotheby's", hammer: '$890K', date: 'Jan 2024', estimate: '$400-600K', reserve: '$400K', premium: '+123%', confidence: 99, provenance: [{ owner: 'European Private Collection', period: '2019-2024' }, { owner: 'Victoria Miro', period: '2019' }], lotNotes: '100% sell-through rate at auction' },
      { work: 'Pastel Dreams', house: "Christie's", hammer: '$720K', date: 'Nov 2023', estimate: '$300-400K', reserve: '$300K', premium: '+140%', confidence: 98, provenance: [{ owner: 'UK Collection', period: '2021-2023' }], lotNotes: null },
    ],
    upcomingAuctions: [
      { work: 'Untitled (Pink)', house: 'Phillips', estimate: '$500-700K', date: 'Apr 15, 2024', lot: '28' },
    ],
    similarArtists: [
      { name: 'Jadé Fadojutimi', slug: 'jade-fadojutimi', similarity: 88 },
      { name: 'Cecily Brown', slug: 'cecily-brown', similarity: 85 },
      { name: 'Jenny Saville', slug: 'jenny-saville', similarity: 79 },
    ],
    aiInsights: [
      { title: 'Perfect Sell-Through', content: '100% sell-through rate demonstrates exceptional market confidence. Every work offered at auction has sold, typically above high estimate.' },
      { title: 'Supply Constraint', content: 'Very limited secondary market availability with only 48 lots recorded. Primary gallery (Victoria Miro) maintains strict allocation. Waitlist for new collectors.' },
      { title: 'Valuation Risk', content: 'Rapid price appreciation from $50K to $2.9M in 3 years signals potential correction risk. Market establishing new price floor after initial speculation.' },
    ],
  },
  'takashi-murakami': {
    name: 'Takashi Murakami',
    nationality: 'Japanese',
    born: 1962,
    tier: 'Blue-chip',
    movements: ['Superflat', 'Neo-Pop'],
    medium: 'Paintings, Sculpture, Prints',
    marketCap: '$890M',
    avgHammer: '$1.2M',
    recordSale: '$15.2M',
    totalLots: '6,234',
    sellThrough: '82%',
    buyInRate: '18%',
    trend: 6.8,
    confidence: 91,
    color: '#E8B4B8',
    bio: 'Takashi Murakami is a Japanese contemporary artist known for blurring the line between high and low culture. His work incorporates anime and manga influences, traditional Japanese painting techniques, and Western art historical references. He coined the term "Superflat" to describe his artistic style and the flattening of distinctions in Japanese consumer culture.',
    notableWorks: [
      { title: 'My Lonesome Cowboy', year: '1998', medium: 'Fiberglass sculpture', lastSale: '$15.2M' },
      { title: '727', year: '1996', medium: 'Acrylic on canvas', lastSale: '$6.2M' },
      { title: 'Flower Ball', year: '2002', medium: 'Acrylic on canvas', lastSale: '$4.8M' },
      { title: 'In the Land of the Dead', year: '2000', medium: 'Acrylic on canvas', lastSale: '$15.2M' },
    ],
    priceHistory: [
      { date: '2019-05', work: 'DOB in Blue', value: 1.2, reserve: 0.8, hammer: 1.2 },
      { date: '2019-11', work: 'Flower Ball', value: 2.1, reserve: 1.5, hammer: 2.1 },
      { date: '2020-10', work: '727-727', value: 4.8, reserve: 3.0, hammer: 4.8 },
      { date: '2021-05', work: 'Superflat First Love', value: 3.5, reserve: 2.0, hammer: 3.5 },
      { date: '2021-11', work: 'Flowers', value: 1.8, reserve: 1.2, hammer: 1.8 },
      { date: '2022-05', work: 'Tan Tan Bo', value: 1.2, reserve: 0.8, hammer: 1.2 },
      { date: '2022-11', work: 'DOB in Strange Forest', value: 1.4, reserve: 1.0, hammer: 1.4 },
      { date: '2023-05', work: 'Flowers (Rainbow)', value: 2.8, reserve: 1.8, hammer: 2.8 },
      { date: '2023-12', work: 'Tan Tan Bo', value: 0.98, reserve: 0.6, hammer: 0.98 },
      { date: '2024-02', work: 'Flowers', value: 2.4, reserve: 1.5, hammer: 2.4 },
    ],
    recentSales: [
      { work: 'Flowers', house: "Christie's", hammer: '$2.4M', date: 'Feb 2024', estimate: '$1.5-2M', reserve: '$1.5M', premium: '+60%', confidence: 98, provenance: [{ owner: 'Asian Private Collection', period: '2010-2024' }, { owner: 'Gagosian Gallery', period: '2008-2010' }], lotNotes: null },
      { work: 'DOB in the Strange Forest', house: "Sotheby's", hammer: '$1.8M', date: 'Jan 2024', estimate: '$1-1.5M', reserve: '$1.0M', premium: '+80%', confidence: 97, provenance: [{ owner: 'European Collection', period: '2005-2024' }], lotNotes: null },
      { work: 'Tan Tan Bo', house: 'Phillips', hammer: '$980K', date: 'Dec 2023', estimate: '$600-800K', reserve: '$600K', premium: '+63%', confidence: 96, provenance: [{ owner: 'American Collection', period: '2012-2023' }], lotNotes: null },
    ],
    upcomingAuctions: [
      { work: 'Skulls', house: "Christie's", estimate: '$2-3M', date: 'May 10, 2024', lot: '15' },
    ],
    similarArtists: [
      { name: 'Yayoi Kusama', slug: 'yayoi-kusama', similarity: 88 },
      { name: 'Yoshitomo Nara', slug: 'yoshitomo-nara', similarity: 92 },
      { name: 'KAWS', slug: 'kaws', similarity: 86 },
    ],
    aiInsights: [
      { title: 'Market Breadth', content: 'One of the most prolific contemporary artists with 6,000+ auction records. Strong print market provides liquidity. Sculpture commands highest prices.' },
      { title: 'Collaboration Premium', content: 'Louis Vuitton collaborations highly sought. Limited edition merchandise showing collector interest. Fashion crossover expands collector base.' },
      { title: 'Regional Dynamics', content: 'Strongest demand from Asian collectors, particularly Japan, Hong Kong, and Korea. Western market stable but less dynamic growth.' },
    ],
  },
  'claude-monet': {
    name: 'Claude Monet',
    nationality: 'French',
    born: 1840,
    died: 1926,
    tier: 'Blue-chip',
    movements: ['Impressionism'],
    medium: 'Paintings',
    marketCap: '$8.2B',
    avgHammer: '$12.4M',
    recordSale: '$110.7M',
    totalLots: '1,847',
    sellThrough: '78%',
    buyInRate: '22%',
    trend: 3.2,
    confidence: 96,
    color: '#6B8E7B',
    bio: 'Claude Monet was a French painter and founder of Impressionist painting. His approach to capturing light and atmosphere, particularly in his series paintings of haystacks, cathedrals, and water lilies, revolutionized Western art and continues to influence artists today.',
    notableWorks: [
      { title: 'Meules', year: '1890', medium: 'Oil on canvas', lastSale: '$110.7M' },
      { title: 'Nymphéas en fleur', year: '1914-17', medium: 'Oil on canvas', lastSale: '$84.7M' },
      { title: 'Le Bassin aux Nymphéas', year: '1919', medium: 'Oil on canvas', lastSale: '$74.0M' },
      { title: 'Nymphéas', year: '1906', medium: 'Oil on canvas', lastSale: '$54.0M' },
    ],
    priceHistory: [
      { date: '2019-05', work: 'Meules', value: 110.7, reserve: 55.0, hammer: 110.7 },
      { date: '2019-11', work: 'Nymphéas', value: 32.0, reserve: 25.0, hammer: 32.0 },
      { date: '2020-10', work: 'Le Bassin aux Nymphéas', value: 40.0, reserve: 35.0, hammer: 40.0 },
      { date: '2021-05', work: 'Meules (Haystacks)', value: 81.3, reserve: 50.0, hammer: 81.3 },
      { date: '2021-11', work: 'Waterloo Bridge', value: 15.2, reserve: 12.0, hammer: 15.2 },
      { date: '2022-05', work: 'Nymphéas en fleur', value: 84.7, reserve: 60.0, hammer: 84.7 },
      { date: '2022-11', work: 'Le Parlement', value: 28.5, reserve: 22.0, hammer: 28.5 },
      { date: '2023-05', work: 'Giverny', value: 22.4, reserve: 18.0, hammer: 22.4 },
      { date: '2023-10', work: 'Waterloo Bridge', value: 18.4, reserve: 15.0, hammer: 18.4 },
      { date: '2024-01', work: 'Nymphéas', value: 74.0, reserve: 65.0, hammer: 74.0 },
    ],
    recentSales: [
      { work: 'Nymphéas', house: "Christie's", hammer: '$74.0M', date: 'Jan 2024', estimate: '$65-85M', reserve: '$65.0M', premium: '+14%', confidence: 99, provenance: [{ owner: 'Distinguished European Collection', period: '1950-2024' }, { owner: 'Galerie Durand-Ruel', period: '1920-1950' }], lotNotes: 'Fresh to market after 70 years in same collection' },
      { work: 'Le Parlement, soleil couchant', house: "Sotheby's", hammer: '$32.8M', date: 'Nov 2023', estimate: '$25-35M', reserve: '$25.0M', premium: '+31%', confidence: 98, provenance: [{ owner: 'American Private Collection', period: '1985-2023' }], lotNotes: 'Exhibited at multiple major museums' },
      { work: 'Waterloo Bridge', house: "Christie's", hammer: '$18.4M', date: 'Oct 2023', estimate: '$15-20M', reserve: '$15.0M', premium: '+23%', confidence: 97, provenance: [{ owner: 'UK Private Collection', period: '1975-2023' }], lotNotes: null },
    ],
    upcomingAuctions: [
      { work: 'Les Peupliers', house: "Sotheby's", estimate: '$20-30M', date: 'May 8, 2024', lot: '5' },
    ],
    similarArtists: [
      { name: 'Pierre-Auguste Renoir', slug: 'pierre-auguste-renoir', similarity: 94 },
      { name: 'Camille Pissarro', slug: 'camille-pissarro', similarity: 91 },
      { name: 'Edgar Degas', slug: 'edgar-degas', similarity: 88 },
    ],
    aiInsights: [
      { title: 'Market Trophy', content: 'Monet remains the most valuable Impressionist at auction. Water Lilies (Nymphéas) series commands highest prices. Works from Giverny period most desirable.' },
      { title: 'Price Stability', content: 'Market shows stable 3.2% growth with strong institutional backing. Buy-in rate of 22% reflects ambitious estimates at top end. Mid-market works ($5-15M) most liquid.' },
      { title: 'Provenance Impact', content: 'Works with distinguished exhibition history command 20-30% premium. Durand-Ruel provenance particularly valued. Fresh-to-market pieces generate strongest bidding.' },
    ],
  },
}

function ShareButton({ title }: { title?: string }) {
  const [copied, setCopied] = useState(false)

  const handleShare = async () => {
    const shareUrl = window.location.href
    const shareTitle = title || 'Artist - salle.art'

    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, url: shareUrl })
      } catch (err) {}
    } else {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <button
      onClick={handleShare}
      className="pill text-sm flex items-center gap-2"
      style={{ background: 'transparent', border: '1px solid var(--border-standard)', color: 'var(--text-primary)' }}
    >
      {copied ? (
        <>
          <svg className="w-4 h-4 text-positive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
          </svg>
          Copied!
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          Share
        </>
      )}
    </button>
  )
}

export default function ArtistPage({ params }: { params: Promise<{ slug: string }> }) {
  const { slug } = use(params)
  const { alertsRemaining } = useAuth()

  const artist = artistData[slug] || {
    name: slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    nationality: 'Unknown',
    born: 1900,
    tier: 'Emerging',
    movements: ['Contemporary'],
    medium: 'Mixed Media',
    marketCap: '$10M',
    avgHammer: '$50K',
    recordSale: '$500K',
    totalLots: '100',
    sellThrough: '75%',
    buyInRate: '25%',
    trend: 5.0,
    confidence: 80,
    color: '#A49A8E',
    bio: 'Artist biography coming soon.',
    notableWorks: [],
    priceHistory: [],
    recentSales: [],
    upcomingAuctions: [],
    similarArtists: [],
    aiInsights: [],
  }

  const isInWatchlist = false

  return (
    <div className="px-4 md:px-6 py-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-text-tertiary mb-6">
        <Link href="/app/artists" className="hover:text-text-secondary transition-colors">
          Artists
        </Link>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-text-primary">{artist.name}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="font-display text-3xl md:text-4xl font-light">{artist.name}</h1>
            <span
              className="text-xs px-2 py-1 rounded-full"
              style={{ background: 'var(--positive)', color: 'white' }}
            >
              {artist.confidence}% confidence
            </span>
          </div>
          <p className="text-text-secondary mb-3">
            {artist.nationality}, {artist.born}{artist.died ? `–${artist.died}` : ''}
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            <span className={`text-xs px-2 py-1 rounded-full ${
              artist.tier === 'Blue-chip'
                ? 'bg-accent-primary/10 text-accent-primary'
                : artist.tier === 'Mid-market'
                ? 'bg-accent-warm/10 text-accent-warm'
                : 'bg-positive/10 text-positive'
            }`}>
              {artist.tier}
            </span>
            {artist.movements.map((movement) => (
              <span key={movement} className="text-xs px-2 py-1 rounded-full border border-border text-text-secondary">
                {movement}
              </span>
            ))}
          </div>
          <p className="text-text-tertiary text-sm">{artist.medium}</p>
        </div>

        <div className="flex flex-wrap gap-3">
          <ShareButton title={`${artist.name} - salle.art`} />

          <button
            className={`pill text-sm flex items-center gap-2 ${isInWatchlist ? 'bg-accent-primary text-white' : ''}`}
            style={!isInWatchlist ? {
              background: 'transparent',
              border: '1px solid var(--border-standard)',
              color: 'var(--text-primary)',
            } : {}}
          >
            <svg className="w-4 h-4" fill={isInWatchlist ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
            {isInWatchlist ? 'Watching' : 'Add to Watchlist'}
          </button>

          <button
            className="pill text-sm flex items-center gap-2"
            style={{
              background: 'transparent',
              border: '1px solid var(--border-standard)',
              color: 'var(--text-primary)',
            }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            Set Alert
            {alertsRemaining > 0 && (
              <span className="text-xs text-text-tertiary">({alertsRemaining} left)</span>
            )}
          </button>
        </div>
      </div>

      {/* Bio */}
      {artist.bio && (
        <div className="card p-5 mb-6">
          <p className="text-sm text-text-secondary leading-relaxed">{artist.bio}</p>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {[
          { label: 'Market Cap', value: artist.marketCap },
          { label: 'Avg Hammer', value: artist.avgHammer },
          { label: 'Record Sale', value: artist.recordSale },
          { label: 'Total Lots', value: artist.totalLots },
          { label: 'Sell-through', value: artist.sellThrough },
          { label: 'Buy-in Rate', value: artist.buyInRate },
        ].map((stat) => (
          <div key={stat.label} className="card p-4">
            <p className="text-text-tertiary text-xs mb-1">{stat.label}</p>
            <p className="font-display text-xl font-light tabular-nums">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* AI Insights - Premium Feature */}
      {artist.aiInsights.length > 0 && (
        <div
          className="rounded-2xl p-6 mb-8 border"
          style={{
            background: 'linear-gradient(135deg, rgba(184, 92, 56, 0.08) 0%, rgba(43, 69, 112, 0.06) 100%)',
            borderColor: 'rgba(184, 92, 56, 0.2)'
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--accent-primary)', color: 'white' }}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h2 className="font-medium">AI Insights</h2>
            </div>
            <TierBadge tier="collector" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {artist.aiInsights.map((insight, i) => (
              <div key={i} className="bg-white/50 dark:bg-black/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                <h3 className="font-medium text-sm mb-2">{insight.title}</h3>
                <p className="text-xs text-text-secondary leading-relaxed">{insight.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notable Works */}
      {artist.notableWorks.length > 0 && (
        <div className="card p-6 mb-8">
          <h2 className="font-medium mb-4">Notable Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {artist.notableWorks.map((work, i) => (
              <div key={i} className="bg-page rounded-xl p-4 flex items-start justify-between">
                <div>
                  <p className="font-medium text-sm">{work.title}</p>
                  <p className="text-xs text-text-tertiary">{work.year} • {work.medium}</p>
                </div>
                {work.lastSale && (
                  <div className="text-right">
                    <p className="text-xs text-text-tertiary">Last Sale</p>
                    <p className="text-sm font-medium tabular-nums">{work.lastSale}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Auctions */}
      {artist.upcomingAuctions.length > 0 && (
        <div className="card p-6 mb-8 border-l-4" style={{ borderLeftColor: 'var(--accent-warm)' }}>
          <div className="flex items-center gap-2 mb-4">
            <svg className="w-5 h-5" style={{ color: 'var(--accent-warm)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h2 className="font-medium">Upcoming Auctions</h2>
          </div>
          <div className="space-y-3">
            {artist.upcomingAuctions.map((auction, i) => (
              <div key={i} className="flex items-center justify-between py-3 border-b border-border-subtle last:border-0">
                <div>
                  <p className="font-medium text-sm">{auction.work}</p>
                  <p className="text-xs text-text-tertiary">{auction.house} • Lot {auction.lot}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium" style={{ color: 'var(--accent-warm)' }}>{auction.date}</p>
                  <p className="text-xs text-text-tertiary">Est. {auction.estimate}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Sales - Premium Feature with Provenance */}
      {artist.recentSales.length > 0 && (
        <div
          className="rounded-2xl p-6 mb-8 border"
          style={{
            background: 'linear-gradient(135deg, rgba(43, 69, 112, 0.08) 0%, rgba(184, 92, 56, 0.06) 100%)',
            borderColor: 'rgba(43, 69, 112, 0.2)'
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium">Recent Sales with Provenance</h2>
            <div className="flex items-center gap-2">
              <span className="text-xs text-text-tertiary">Click row to expand</span>
              <TierBadge tier="gallery" />
            </div>
          </div>
          <div className="overflow-x-auto bg-white/50 dark:bg-black/20 backdrop-blur-sm rounded-xl border border-white/30">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-subtle">
                  <th className="text-left py-3 px-4 text-xs font-medium text-text-tertiary">Work</th>
                  <th className="text-left py-3 px-2 text-xs font-medium text-text-tertiary">House</th>
                  <th className="text-right py-3 px-2 text-xs font-medium text-text-tertiary">Reserve</th>
                  <th className="text-right py-3 px-2 text-xs font-medium text-text-tertiary">Hammer</th>
                  {artist.recentSales[0]?.premium && (
                    <th className="text-center py-3 px-2 text-xs font-medium text-text-tertiary">Premium</th>
                  )}
                  {artist.recentSales[0]?.confidence && (
                    <th className="text-center py-3 px-2 text-xs font-medium text-text-tertiary">Conf.</th>
                  )}
                  <th className="text-right py-3 px-4 text-xs font-medium text-text-tertiary">Date</th>
                </tr>
              </thead>
              <tbody>
                {artist.recentSales.map((sale, i) => (
                  <SaleRow key={i} sale={sale} artistColor={artist.color} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Price History Chart - Premium Feature */}
      {artist.priceHistory && artist.priceHistory.length > 0 && (
        <div
          className="rounded-2xl p-6 mb-8 border"
          style={{
            background: 'linear-gradient(135deg, rgba(75, 124, 89, 0.08) 0%, rgba(43, 69, 112, 0.06) 100%)',
            borderColor: 'rgba(75, 124, 89, 0.2)'
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium">Price History by Work</h2>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                {['1Y', '3Y', '5Y', 'All'].map((period) => (
                  <button
                    key={period}
                    className={`px-3 py-1 rounded-full text-xs transition-all ${
                      period === '5Y'
                        ? 'bg-text-primary text-white'
                        : 'bg-transparent border border-border text-text-secondary hover:border-border-hover'
                    }`}
                  >
                    {period}
                  </button>
                ))}
              </div>
              <TierBadge tier="collector" />
            </div>
          </div>

          <div className="bg-white/50 dark:bg-black/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-text-tertiary">Individual sale results over time</p>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{ background: artist.color }} />
                  Hammer
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full border border-text-tertiary" />
                  Reserve
                </span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <ScatterChart margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#A49A8E', fontSize: 10 }}
                  tickFormatter={(val) => val.split('-')[0]}
                />
                <YAxis
                  dataKey="value"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#A49A8E', fontSize: 10 }}
                  tickFormatter={(val) => `$${val}M`}
                  domain={['dataMin - 1', 'dataMax + 2']}
                />
                <Tooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload
                      return (
                        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
                          <p className="font-medium text-sm mb-1">{data.work}</p>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                            <span className="text-text-tertiary">Reserve:</span>
                            <span className="text-right">${data.reserve}M</span>
                            <span className="text-text-tertiary">Hammer:</span>
                            <span className="text-right font-medium">${data.hammer}M</span>
                            <span className="text-text-tertiary">Premium:</span>
                            <span className="text-right" style={{ color: 'var(--positive)' }}>
                              +{Math.round(((data.hammer - data.reserve) / data.reserve) * 100)}%
                            </span>
                          </div>
                          <p className="text-xs text-text-muted mt-2">{data.date}</p>
                        </div>
                      )
                    }
                    return null
                  }}
                />
                {/* Reserve price (hollow circles) */}
                <Scatter
                  data={artist.priceHistory}
                  dataKey="reserve"
                  fill="transparent"
                  stroke="#A49A8E"
                  strokeWidth={1.5}
                  shape="circle"
                >
                  {artist.priceHistory.map((entry, index) => (
                    <Cell key={`reserve-${index}`} />
                  ))}
                </Scatter>
                {/* Hammer price (filled circles) */}
                <Scatter
                  data={artist.priceHistory}
                  fill={artist.color}
                >
                  {artist.priceHistory.map((entry, index) => (
                    <Cell key={`hammer-${index}`} fill={artist.color} />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Similar Artists - Premium Feature */}
      {artist.similarArtists.length > 0 && (
        <div
          className="rounded-2xl p-6 border"
          style={{
            background: 'linear-gradient(135deg, rgba(139, 58, 42, 0.08) 0%, rgba(184, 92, 56, 0.06) 100%)',
            borderColor: 'rgba(139, 58, 42, 0.2)'
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-medium">Comparable Artists</h2>
            <TierBadge tier="collector" />
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {artist.similarArtists.map((similar) => {
              // Generate color and initials from name
              const initials = similar.name.split(' ').map(n => n[0]).join('').slice(0, 2)
              const colors = ['#E63946', '#4ECDC4', '#7B68EE', '#FF69B4', '#4A90D9', '#9B7EBD']
              const color = colors[similar.name.length % colors.length]
              return (
                <Link
                  key={similar.slug}
                  href={`/app/artists/${similar.slug}`}
                  className="group p-3 rounded-xl bg-white/50 dark:bg-black/20 backdrop-blur-sm border border-white/30 hover:border-accent-primary/50 transition-all flex items-center gap-3"
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-medium flex-shrink-0"
                    style={{ background: color }}
                  >
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{similar.name}</p>
                    <p className="text-xs text-text-tertiary">{similar.similarity}% similar</p>
                  </div>
                </Link>
              )
            })}
          </div>
        </div>
      )}

      {/* Similar Investment by Tier */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-medium">Investment Alternatives by Tier</h2>
          <span className="text-xs text-text-tertiary">Based on {artist.tier}</span>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {/* Same Tier */}
          <div>
            <p className="text-xs text-text-tertiary mb-2 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ background: 'var(--accent-primary)' }} />
              Same tier ({artist.tier})
            </p>
            <div className="space-y-2">
              {artist.similarArtists.slice(0, 2).map((a) => {
                const initials = a.name.split(' ').map(n => n[0]).join('').slice(0, 2)
                const colors = ['#E63946', '#4ECDC4', '#7B68EE', '#FF69B4', '#4A90D9']
                const color = colors[a.name.length % colors.length]
                return (
                  <Link key={a.slug} href={`/app/artists/${a.slug}`} className="flex items-center gap-2 py-1 hover:opacity-80 transition-opacity">
                    <div className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-medium" style={{ background: color }}>
                      {initials}
                    </div>
                    <span className="text-sm truncate">{a.name}</span>
                  </Link>
                )
              })}
            </div>
          </div>

          {/* One tier up */}
          <div>
            <p className="text-xs text-text-tertiary mb-2 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ background: 'var(--positive)' }} />
              Higher tier
            </p>
            <div className="space-y-2">
              {[
                { name: 'Pablo Picasso', slug: 'pablo-picasso' },
                { name: 'Claude Monet', slug: 'claude-monet' },
              ].slice(0, 2).map((a) => {
                const initials = a.name.split(' ').map(n => n[0]).join('').slice(0, 2)
                const colors = ['#9B7EBD', '#F4A460', '#88B04B']
                const color = colors[a.name.length % colors.length]
                return (
                  <Link key={a.slug} href={`/app/artists/${a.slug}`} className="flex items-center gap-2 py-1 hover:opacity-80 transition-opacity">
                    <div className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-medium" style={{ background: color }}>
                      {initials}
                    </div>
                    <span className="text-sm truncate">{a.name}</span>
                  </Link>
                )
              })}
            </div>
          </div>

          {/* One tier down (value plays) */}
          <div>
            <p className="text-xs text-text-tertiary mb-2 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full" style={{ background: 'var(--accent-warm)' }} />
              Value plays
            </p>
            <div className="space-y-2">
              {[
                { name: 'Amoako Boafo', slug: 'amoako-boafo' },
                { name: 'Flora Yukhnovich', slug: 'flora-yukhnovich' },
              ].slice(0, 2).map((a) => {
                const initials = a.name.split(' ').map(n => n[0]).join('').slice(0, 2)
                const colors = ['#4ECDC4', '#E85D75', '#6B5B95']
                const color = colors[a.name.length % colors.length]
                return (
                  <Link key={a.slug} href={`/app/artists/${a.slug}`} className="flex items-center gap-2 py-1 hover:opacity-80 transition-opacity">
                    <div className="w-7 h-7 rounded-md flex items-center justify-center text-white text-xs font-medium" style={{ background: color }}>
                      {initials}
                    </div>
                    <span className="text-sm truncate">{a.name}</span>
                  </Link>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
